import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  useEffect,
} from 'react'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { PolicyRecord } from '@trustero/trustero-api-web/lib/model/policy_pb'
import { useModalState } from 'src/Modal/ModalStateContext'
import { ModalFormId } from '../../components/ModalForms/ModalForm'
import { useControlsForComplianceFramework } from '../../components/async/complianceframework/useControlsForComplianceFramework'
import { usePoliciesForComplianceFramework } from '../../components/async/complianceframework/usePoliciesForComplianceFramework'
import { useControlsInAccountForComplianceFramework } from '../../components/async/complianceframework/useControlsInAccountForComplianceFramework'
import { usePoliciesInAccountForComplianceFramework } from '../../components/async/complianceframework/usePoliciesInAccountForComplianceFramework'

interface FrameworkWizard {
  frameworkState: {
    framework: ComplianceFramework | null
    setFramework: React.Dispatch<
      React.SetStateAction<ComplianceFramework | null>
    >
  }
  controlsState: {
    suggestedControls: Control[]
    setSuggestedControls: React.Dispatch<React.SetStateAction<Control[]>>
    selectedControls: string[]
    setSelectedControls: React.Dispatch<React.SetStateAction<string[]>>
    controlsInAccount: Control[]
    setControlsInAccount: React.Dispatch<React.SetStateAction<Control[]>>
  }
  policiesState: {
    suggestedPolicies: PolicyRecord[]
    setSuggestedPolicies: React.Dispatch<React.SetStateAction<PolicyRecord[]>>
    selectedPolicies: string[]
    setSelectedPolicies: React.Dispatch<React.SetStateAction<string[]>>
    policiesInAccount: PolicyRecord[]
    setPoliciesInAccount: React.Dispatch<React.SetStateAction<PolicyRecord[]>>
  }
  loadingState: {
    loading: boolean
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
  }
}

export const FrameworkWizardContext = createContext<FrameworkWizard>({
  frameworkState: {
    framework: null,
    setFramework: () => null,
  },
  controlsState: {
    suggestedControls: [],
    setSuggestedControls: () => null,
    selectedControls: [],
    setSelectedControls: () => null,
    controlsInAccount: [],
    setControlsInAccount: () => null,
  },
  policiesState: {
    suggestedPolicies: [],
    setSuggestedPolicies: () => null,
    selectedPolicies: [],
    setSelectedPolicies: () => null,
    policiesInAccount: [],
    setPoliciesInAccount: () => null,
  },
  loadingState: {
    loading: true,
    setLoading: () => null,
  },
})

export const useFrameworkWizard = (): FrameworkWizard => {
  // The catalog wizard is a single modal; fetch only while it is open.
  const { activeModal } = useModalState()
  const isActive = activeModal === ModalFormId.CHOOSE_COMPLIANCE_FRAMEWORK
  const frameworkWizardState = useContext(FrameworkWizardContext)
  const {
    frameworkState: { framework },
    controlsState: { setSuggestedControls, setControlsInAccount },
    policiesState: { setSuggestedPolicies, setPoliciesInAccount },
    loadingState: { setLoading },
  } = frameworkWizardState

  // ------- Getting data for the wizard once we have the framework --------
  // controls for framework already in the account
  const controlsInAccountResponse = useControlsInAccountForComplianceFramework(
    framework?.getModelId() || '',
    isActive,
  )
  useEffect(() => {
    setControlsInAccount(controlsInAccountResponse.data?.getItemsList() || [])
  }, [setControlsInAccount, controlsInAccountResponse.data])

  // suggested controls to add
  const controlsResponse = useControlsForComplianceFramework(
    framework?.getModelId() || '',
    isActive,
  )
  useEffect(() => {
    setSuggestedControls(controlsResponse.data?.getItemsList() || [])
  }, [setSuggestedControls, controlsResponse.data])

  // policies for framework already in the account
  const policiesInAccountResponse = usePoliciesInAccountForComplianceFramework(
    framework?.getModelId() || '',
    isActive,
  )
  useEffect(() => {
    setPoliciesInAccount(policiesInAccountResponse.data?.getItemsList() || [])
  }, [setPoliciesInAccount, policiesInAccountResponse.data])

  // suggested policies to add
  const policiesResponse = usePoliciesForComplianceFramework(
    framework?.getModelId() || '',
    isActive,
  )
  useEffect(() => {
    setSuggestedPolicies(policiesResponse.data?.getItemsList() || [])
  }, [setSuggestedPolicies, policiesResponse.data])

  // --------
  // -------- Controlling the loading state ----------
  useEffect(() => {
    if (
      controlsInAccountResponse.loading ||
      controlsResponse.loading ||
      policiesResponse.loading ||
      policiesInAccountResponse.loading
    ) {
      setLoading(true)
    } else {
      setLoading(false)
    }
  }, [
    controlsInAccountResponse.loading,
    controlsResponse.loading,
    policiesResponse.loading,
    policiesInAccountResponse.loading,
    setLoading,
    framework,
  ])
  // -------
  return frameworkWizardState
}

export const FrameworkContextProvider = ({
  children,
}: {
  children: JSX.Element
}): JSX.Element => {
  const [framework, setFramework] = useState<ComplianceFramework | null>(null)
  const [suggestedControls, setSuggestedControls] = useState<Control[]>([])
  const [selectedControls, setSelectedControls] = useState<string[]>([])
  const [controlsInAccount, setControlsInAccount] = useState<Control[]>([])
  const [suggestedPolicies, setSuggestedPolicies] = useState<PolicyRecord[]>([])
  const [selectedPolicies, setSelectedPolicies] = useState<string[]>([])
  const [policiesInAccount, setPoliciesInAccount] = useState<PolicyRecord[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  const wizardData: FrameworkWizard = useMemo(
    () => ({
      frameworkState: {
        framework,
        setFramework,
      },
      controlsState: {
        suggestedControls,
        setSuggestedControls,
        selectedControls,
        setSelectedControls,
        controlsInAccount,
        setControlsInAccount,
      },
      policiesState: {
        suggestedPolicies,
        setSuggestedPolicies,
        selectedPolicies,
        setSelectedPolicies,
        policiesInAccount,
        setPoliciesInAccount,
      },
      loadingState: {
        loading,
        setLoading,
      },
    }),
    [
      framework,
      suggestedControls,
      selectedControls,
      controlsInAccount,
      suggestedPolicies,
      selectedPolicies,
      policiesInAccount,
      loading,
    ],
  )

  return (
    <FrameworkWizardContext.Provider value={wizardData}>
      {children}
    </FrameworkWizardContext.Provider>
  )
}
